import { Card, Divider, Pagination, Button, message } from 'antd';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { CSVLink } from 'react-csv';
import { DownloadOutlined } from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '../../methods/redux/hooks';
import {
	retrieveSwapRequest,
	retrieveSwapRequestCount,
} from '../../methods/redux/reducers/swap';
import baseuri from '../../methods/baseuri';
import {
	getCurrencies,
	getBlockchainNetworks,
} from '../../methods/redux/reducers/currency';
import SwapItem from './SwapItem';
import SearchFilter from '../../component/Filter';
import SwapStatusCount from './SwapCountByStatus';
import { getUserProfileById } from '../../methods/redux/reducers/user-profile';
import remakeQueryString from '../../methods/utils/remake-query-string';

interface SelectOption {
	name: string;
	value: string;
}

const DownloadButton = styled(Button)`
	color: red;
	background-color: #f5d5d5;
	border-radius: 5px;
	&:hover {
		color: red;
		background-color: #efe7e7;
	}
	&:focus {
		color: red;
		background-color: #f5d5d5;
	}
`;

const Swap = () => {
	const dispatch = useAppDispatch();
	const [searchParams, setSearchParams] = useSearchParams();
	const { userId } = useParams();
	const [loading, setLoading] = useState(false);
	const [results, setResults] = useState([] as any);

	const { user } = useAppSelector((store) => store.userProfile);
	const { currencies } = useAppSelector((store) => store.currencies);
	const { swapRequests, swapRequestPaginator } = useAppSelector(
		(store) => store.swap
	);
	const getQueryParams = () => Object.fromEntries(searchParams.entries());
	const params = Object.fromEntries(searchParams.entries());

	const csvLinkRef = useRef<
		CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }
	>(null);

	const currencySelect: SelectOption[] = currencies.map((item: any) => ({
		name: item.symbol,
		value: String(item._id), // Ensure string
	}));

	useEffect(() => {
		const queryData = Object.fromEntries(searchParams.entries());
		if (Object.keys(queryData).length === 0) {
			setSearchParams({
				swapStatus: 'pending',
				pageId: '1',
				perPage: '20',
			});
		}
		if (userId) {
			dispatch(retrieveSwapRequestCount({ userId }));
		} else {
			dispatch(retrieveSwapRequestCount({}));
		}

		dispatch(getBlockchainNetworks());
		dispatch(getCurrencies({}));
	}, []);

	const updateQueryString = (query: any, action = 'update') => {
		remakeQueryString(query, searchParams, setSearchParams, action);
	};

	useEffect(() => {
		const queryData = Object.fromEntries(searchParams.entries());
		if (Object.keys(queryData).length > 0) {
			if (userId) {
				dispatch(retrieveSwapRequest({ ...queryData, userId }));
			} else {
				dispatch(retrieveSwapRequest(queryData));
			}
		}
	}, [searchParams]);

	useEffect(() => {
		if (userId) {
			dispatch(getUserProfileById(userId));
		}
	}, [userId]);

	// Transform the results data to match the headers
	const transformSwapRequests = (requests: any[]) =>
		requests?.map((data: any) => ({
			baseCurrencyAmount: data?.baseCurrencyAmount,
			baseCurrencySymbol: data?.baseCurrency?.symbol,
			quoteCurrencySymbol: data?.quoteCurrency?.symbol,
			quoteCurrencyAmountLower: data?.resultQuoteCurrencyAmountLower,
			quoteCurrencyAmountUpper: data?.resultQuoteCurrencyAmountUpper,
			fee: `${data?.swapData?.transactionFeeInBaseCurrencyAmount} ${data.swapData?.baseSymbol}`,
			status: data?.swapStatus,
			customer: data?.userId?.username,
			swapDeadline: data?.swapDeadlineMinutes,
			transactionSpeed: data?.transactionSpeed,
			createdAt: moment(data?.createdAt).format('lll'), // Handle computed value
			processedBy: `${
				data?.processedByUserId?.email ? data?.processedByUserId?.email : '--'
			}`,
		}));

	// Define headers with simple keys
	const headers = [
		{ label: 'Base Amount', key: 'baseCurrencyAmount' },
		{ label: 'Base Currency', key: 'baseCurrencySymbol' },
		{ label: 'Quote Currency', key: 'quoteCurrencySymbol' },
		{
			label: 'Quote Amount Lower',
			key: 'quoteCurrencyAmountLower',
		},
		{
			label: 'Quote Amount Upper',
			key: 'quoteCurrencyAmountUpper',
		},
		{ label: 'Swap Fee (Base Currency)', key: 'fee' },
		{ label: 'Swap Status', key: 'status' },
		{ label: 'Customer', key: 'customer' },
		{ label: 'Swap Deadline(Mins)', key: 'swapDeadline' },
		{ label: 'Transaction Speed', key: 'transactionSpeed' },
		{ label: 'Created At', key: 'createdAt' },
		{ label: 'Processed By', key: 'processedBy' },
	];

	const handleDownload = () => {
		setLoading(true);
		const params1 = getQueryParams();
		setResults([]);
		baseuri
			.get('/admin/swap' || '', {
				params: {
					...params1,
					perPage: swapRequestPaginator.itemCount || 0,
					pageId: '1',
				},
			})
			.then((res: any) => {
				const rawData = res.data.data.itemsList;
				const transformedData = transformSwapRequests(rawData);
				setResults(transformedData);
				setLoading(false);
			})
			.catch(() => {
				message.error('Could not download CSV');
				setLoading(false);
			});
	};

	useEffect(() => {
		if (results.length > 0) {
			csvLinkRef?.current?.link.click();
		}
	}, [results]);

	return (
		<div>
			<Card>
				<h1>
					Currency Swap{' '}
					{userId && (
						<span>
							for{' '}
							<Link to={`/user-profile/${user.profile?._id}`}>
								@{user.profile?.username}
							</Link>
						</span>
					)}
				</h1>
				<p>
					Swap transactions can take place within the application, on the
					blockchain, and by a swap robot running at a different location.
				</p>

				<br />

				<div>
					<Link to="/settings/swap">Settings</Link>
				</div>

				<br />

				<SwapStatusCount />

				<br />

				<SearchFilter
					title="Filter Swap Records"
					searchFilters={[
						{
							label: 'Status',
							name: 'swapStatus',
							htmlElement: 'select',
							defaultSelectedValue: 'pending',
							selectOptions: [
								{ value: 'review', name: 'Review' },
								{ value: 'pending', name: 'Pending' },
								{ value: 'hold', name: 'Hold' },
								{ value: 'processing', name: 'Processing' },
								{ value: 'completed', name: 'Completed' },
								{ value: 'reverted', name: 'Reverted' },
								{ value: 'cancelled', name: 'Cancelled' },
								{ value: 'deleted', name: 'Deleted' },
							],
						},
						{
							label: 'Base Currency',
							name: 'baseCurrency',
							htmlElement: 'select',
							selectOptions: currencySelect,
						},
						{
							label: 'Quote Currency',
							name: 'quoteCurrency',
							htmlElement: 'select',
							selectOptions: currencySelect,
						},
						{
							label: 'User Id',
							name: 'userId',
							htmlElement: 'input',
						},
						{
							label: 'Processed By User Id',
							name: 'processedByUserId',
							htmlElement: 'input',
						},
						{
							label: 'Swap Protocol',
							name: 'swapProtocol',
							htmlElement: 'input',
						},
						{
							label: 'Transaction Blockchain Id',
							name: 'transactionBlockchainId',
							htmlElement: 'input',
						},
						{
							label: 'Transaction Speed',
							name: 'transactionSpeed',
							htmlElement: 'input',
						},
						{
							label: 'Transaction Hash',
							name: 'transactionHash',
							htmlElement: 'input',
						},
						{
							label: 'Start Date',
							name: 'startDate',
							htmlElement: 'datepicker',
						},
						{
							label: 'End Date',
							name: 'endDate',
							htmlElement: 'datepicker',
						},
					]}
					retrieveQueryString={updateQueryString}
				/>
				<Divider />
				<p>
					Total: <strong>{swapRequests.length}</strong> items
				</p>
				<br />

				<Pagination
					onChange={(pageId) => updateQueryString({ pageId })}
					current={swapRequestPaginator.currentPage}
					total={swapRequestPaginator.itemCount}
					pageSize={swapRequestPaginator.perPage}
				/>

				{swapRequests.length > 0 && (
					<div style={{ display: 'flex', justifyContent: 'flex-end' }}>
						<DownloadButton
							danger
							loading={loading}
							icon={<DownloadOutlined />}
							onClick={() => handleDownload()}
						>
							{loading ? 'Loading csv...' : 'Download CSV'}
						</DownloadButton>

						<CSVLink
							data={results}
							headers={headers}
							className="download"
							filename={`CurrencySwap-${params?.swapStatus}.csv`}
							ref={csvLinkRef}
						/>
					</div>
				)}
				<br />

				{swapRequests.map((item) => (
					<SwapItem swapItem={item} />
				))}
				<Divider />
				<Pagination
					onChange={(pageId) => updateQueryString({ pageId })}
					current={swapRequestPaginator.currentPage}
					pageSize={swapRequestPaginator.perPage}
					total={swapRequestPaginator.itemCount}
				/>
			</Card>
		</div>
	);
};

export default Swap;
